import {
  Phone,
  MapPin,
  Clock,
  Bike,
  ArrowDown,
  Flame,
  Utensils,
} from "lucide-react";
import { BUSINESS } from "@/data/rosto";
import { useOrderModal } from "@/components/OrderModal";

/* ==================================================
   MENU DATA — ROSTO'S ufficiale
   ================================================== */

const ARROSTI = [
  {
    id: "pollo-campese",
    title: "Pollo Campese",
    tagline: "Allevato all'aperto · Marinato 48h",
    image: "/pollo-intero.jpg",
    rows: [
      { label: "Un quarto", price: "4,90" },
      { label: "Mezzo", price: "8,90" },
      { label: "Intero", price: "14,90" },
    ],
    badge: "Il classico",
  },
  {
    id: "tacchino",
    title: "Tacchino arrosto",
    tagline: "Marinatura mediterranea · Cottura lenta",
    image: "/tacchino-arrosto.jpg",
    rows: [
      { label: "200 g", price: "8,90" },
      { label: "400 g", price: "14,90" },
      { label: "Intero", price: "29,90/kg" },
    ],
    badge: "Novità",
  },
  {
    id: "salsicciotto",
    title: "Salsicciotto di suino",
    tagline: "Suino allevato allo stato brado",
    image: "/salsicciotto.jpg",
    rows: [
      { label: "1 salsicciotto (100 g)", price: "5,90" },
      { label: "2 salsicciotti", price: "9,90" },
      { label: "3 salsicciotti", price: "12,90" },
    ],
    badge: "Brado",
  },
];

const PANINI = [
  {
    id: "campese",
    title: "Campese",
    price: "8,90",
    image: "/panino-campese.jpg",
  },
  {
    id: "tacchino-panino",
    title: "Tacchino arrosto",
    price: "8,90",
    image: "/panino-tacchino.jpg",
  },
  {
    id: "salsicciotto-panino",
    title: "Salsicciotto brado",
    price: "8,90",
  },
  {
    id: "insalata-pollo",
    title: "Insalata di pollo",
    price: "6,90",
    image: "/panino-insalata-pollo.jpg",
  },
];

const AL_PIATTO_COMBOS = [
  { id: "piatto-1", label: "Arrosto + 1 contorno", price: "9,90" },
  { id: "piatto-2", label: "Arrosto + 2 contorni", price: "12,90" },
];

const AGGIUNTE = [
  {
    id: "formaggio",
    label: "Formaggio a scelta",
    price: "+ €1,00",
    note: "Provola · Feta · Parmigiano · Pecorino",
  },
  { id: "salsa", label: "Salsa extra", price: "+ €0,50" },
  { id: "arrosto-extra", label: "Arrosto extra", price: "+ €3,00" },
  { id: "verdure-extra", label: "Verdure o fermentati extra", price: "+ €1,00" },
];

const CONTORNI = [
  { id: "riso", title: "Riso bianco", regular: "3,00", large: "4,50", image: "/riso.jpg" },
  { id: "patate", title: "Patate arrosto", regular: "3,50", large: "5,00", image: "/patate-forno.jpg" },
  { id: "verdure", title: "Verdure al forno", regular: "4,00", large: "5,50", image: "/verdure-forno.jpg" },
  { id: "cicoria", title: "Cicoria", regular: "4,00", large: "5,00", image: "/cicoria.jpg" },
  { id: "fermentate", title: "Verdure fermentate", regular: "4,00", large: "5,00", image: "/verdure-fermentate.jpg" },
  { id: "funghi", title: "Funghi", regular: "4,00", large: "5,50", image: "/funghi.jpg" },
];

const SFIZI = [
  {
    id: "nuggets",
    title: "Nuggets",
    sub: "Bocconcini panati croccanti",
    image: "/nuggets.jpg",
    sizes: [
      { label: "6 pz", price: "6,90" },
      { label: "9 pz", price: "9,90" },
    ],
  },
  {
    id: "alette",
    title: "Alette",
    sub: "Al naturale · glassate al miele · glassate piccanti",
    image: "/alette-piccanti.jpg",
    sizes: [
      { label: "6 pz", price: "7,50" },
      { label: "9 pz", price: "10,50" },
    ],
  },
];

const INSALATE = [
  {
    id: "caesar",
    title: "Caesar Campese",
    price: "8,90",
    sub: "Campese, insalata, Parmigiano, crostini, salsa Caesar",
    image: "/caesar-salad.jpg",
  },
  {
    id: "mediterranea",
    title: "Mediterranea",
    price: "8,90",
    sub: "Insalata, ceci, pomodorini, feta, verdure fermentate",
    image: "/mediterranea-salad.jpg",
  },
  {
    id: "mista",
    title: "Insalata mista",
    price: "5,90",
    sub: "Freschezza semplice: insalata, pomodoro, carota",
    image: "/mista-salad.jpg",
  },
];

const SALSE = [
  "Rosto's Signature",
  "Maionese homemade",
  "Piccante homemade",
  "Barbecue",
  "Ketchup",
  "Burger",
  "Senape",
];

/* ==================================================
   HELPERS
   ================================================== */

const Eyebrow = ({ children, testid }) => (
  <div
    data-testid={testid}
    className="font-label text-[11px] text-[#C86C3F] flex items-center gap-3"
  >
    <span className="inline-block w-7 h-px bg-[#C86C3F]" />
    {children}
  </div>
);

const SectionShell = ({ id, testid, eyebrow, title, subtitle, children }) => (
  <section
    id={id}
    data-testid={testid}
    className="px-5 sm:px-8 py-14 sm:py-20 max-w-2xl mx-auto"
  >
    <Eyebrow testid={`${testid}-eyebrow`}>{eyebrow}</Eyebrow>
    <h2 className="font-display text-3xl sm:text-4xl mt-4 leading-[1.05] text-[#F5F1E6]">
      {title}
    </h2>
    {subtitle && (
      <p className="mt-3 text-[#B2A69E] text-sm sm:text-base">{subtitle}</p>
    )}
    <div className="mt-8">{children}</div>
  </section>
);

/* ==================================================
   PAGE
   ================================================== */

export default function MenuLanding() {
  const { open: openOrderModal } = useOrderModal();

  return (
    <main
      data-testid="menu-landing"
      className="min-h-screen bg-[#0F0F0F] text-[#F5F1E6]"
    >
      {/* ------------------------- HEADER ------------------------- */}
      <header
        data-testid="menu-header"
        className="relative overflow-hidden border-b border-[#362A26]"
      >
        <img
          src="/pollo-intero.jpg"
          alt="Miglior pollo arrosto Campese Garbatella Roma — Rosto's"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F0F0F]/85 via-[#0F0F0F]/70 to-[#0F0F0F]" />
        <div className="absolute inset-0 grain" />

        <div className="relative px-5 pt-8 pb-10 sm:px-8 sm:pt-12 sm:pb-14 max-w-2xl mx-auto">
          <a
            href="/"
            className="inline-flex items-center gap-3"
            data-testid="menu-logo-link"
          >
            <img
              src="/rostos-logo.png"
              alt="Rosto's"
              className="h-12 w-12 object-contain"
            />
            <span className="font-display text-2xl text-[#D9531E] uppercase tracking-tight leading-none">
              ROSTO'S
            </span>
          </a>

          <h1
            data-testid="menu-title"
            className="font-display text-4xl sm:text-5xl mt-7 leading-[1.05] text-[#F5F1E6]"
          >
            L'arte
            <br />
            <span className="text-[#E68153]">dell'arrosto</span>
          </h1>
          <p
            data-testid="menu-subtitle"
            className="mt-4 text-[#B2A69E] text-base sm:text-lg"
          >
            Il menu completo di ROSTO'S — Garbatella, Roma.
          </p>

          <div className="mt-8">
            <button
              type="button"
              onClick={openOrderModal}
              data-testid="menu-cta-order"
              className="btn-copper w-full px-6 py-5 rounded-2xl font-label text-sm flex items-center justify-center gap-3"
            >
              <Bike className="w-5 h-5" />
              Ordina a domicilio
            </button>
          </div>

          <p
            data-testid="menu-eta"
            className="mt-5 text-center text-[#B2A69E] text-sm flex items-center justify-center gap-2"
          >
            <Clock className="w-4 h-4 text-[#E68153]" />
            <span>⏱ Pronto in 10–15 minuti</span>
          </p>

          <div className="mt-8 flex justify-center">
            <a
              href="#arrosti"
              className="font-label text-[10px] text-[#B2A69E] flex items-center gap-2 hover:text-[#E68153] transition-colors"
              data-testid="menu-scroll-arrosti"
            >
              Scorri il menu <ArrowDown className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </header>

      {/* ------------------------- ARROSTI ------------------------- */}
      <SectionShell
        id="arrosti"
        testid="arrosti-section"
        eyebrow="1. I nostri arrosti"
        title="L'arrosto fatto come si deve"
        subtitle="Marinati 48 ore, cotti lentamente al forno. Pelle croccante, carne succosa."
      >
        <div className="space-y-4">
          {ARROSTI.map((a) => (
            <article
              key={a.id}
              data-testid={`arrosti-${a.id}`}
              className="card-rosto rounded-2xl overflow-hidden"
            >
              {a.image && (
                <div className="relative w-full h-56 overflow-hidden">
                  <img
                    src={a.image}
                    alt={a.title}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] via-transparent to-transparent" />
                </div>
              )}
              <div className="p-5 sm:p-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <span className="font-label text-[10px] text-[#C86C3F]">
                      {a.badge}
                    </span>
                    <h3 className="font-display text-2xl sm:text-3xl text-[#F5F1E6] mt-1">
                      {a.title}
                    </h3>
                    <p className="text-[#B2A69E] text-xs sm:text-sm mt-1">
                      {a.tagline}
                    </p>
                  </div>
                </div>
                <ul className="mt-5 divide-y divide-[#362A26] border-y border-[#362A26]">
                  {a.rows.map((r) => (
                    <li
                      key={r.label}
                      className="flex items-baseline justify-between py-3"
                      data-testid={`arrosti-${a.id}-${r.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <span className="text-[#F5F1E6] text-base sm:text-lg">
                        {r.label}
                      </span>
                      <span className="font-display text-xl sm:text-2xl text-[#E68153]">
                        €{r.price}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </SectionShell>

      <div className="divider-ember mx-5 sm:mx-8 max-w-2xl xl:mx-auto" />

      {/* ------------------------- NEL PANINO ------------------------- */}
      <SectionShell
        id="panini"
        testid="panini-section"
        eyebrow="2. Nel panino"
        title="I panini di ROSTO'S"
        subtitle="Arrosto, verdure o fermentati e salsa. Pane artigianale, ripieni ricchi."
      >
        <ul className="space-y-3">
          {PANINI.map((p) => (
            <li
              key={p.id}
              data-testid={`panino-${p.id}`}
              className="card-rosto rounded-2xl overflow-hidden flex items-center gap-4 pr-5"
            >
              {p.image ? (
                <img
                  src={p.image}
                  alt={p.title}
                  loading="lazy"
                  className="w-24 h-24 sm:w-28 sm:h-28 object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center flex-shrink-0 bg-[#251D1A] border-r border-[#362A26]">
                  <Utensils className="w-6 h-6 text-[#C86C3F]" />
                </div>
              )}
              <div className="flex items-baseline justify-between gap-3 flex-1 py-4">
                <span className="text-[#F5F1E6] text-lg sm:text-xl font-display">
                  {p.title}
                </span>
                <span className="font-display text-xl sm:text-2xl text-[#E68153] whitespace-nowrap">
                  €{p.price}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </SectionShell>

      <div className="divider-ember mx-5 sm:mx-8 max-w-2xl xl:mx-auto" />

      {/* ------------------------- AL PIATTO ------------------------- */}
      <SectionShell
        id="al-piatto"
        testid="al-piatto-section"
        eyebrow="3. Al piatto"
        title="Scegli il tuo arrosto"
        subtitle="Scegli pollo, tacchino o salsicciotto e abbinalo ai contorni."
      >
        <ul className="divide-y divide-[#362A26] border-y border-[#362A26]">
          {AL_PIATTO_COMBOS.map((c) => (
            <li
              key={c.id}
              data-testid={`piatto-${c.id}`}
              className="flex items-baseline justify-between gap-3 py-4"
            >
              <span className="text-[#F5F1E6] text-base sm:text-lg font-display">
                {c.label}
              </span>
              <span className="font-display text-xl sm:text-2xl text-[#E68153] whitespace-nowrap">
                €{c.price}
              </span>
            </li>
          ))}
        </ul>

        <div className="mt-8">
          <p className="font-label text-[11px] text-[#C86C3F] mb-3">Aggiunte</p>
          <ul className="rounded-2xl border border-[#362A26] bg-[#1A1311] divide-y divide-[#362A26]">
            {AGGIUNTE.map((a) => (
              <li
                key={a.id}
                data-testid={`aggiunta-${a.id}`}
                className="px-4 sm:px-5 py-3"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[#F5F1E6] text-sm sm:text-base">
                    {a.label}
                  </span>
                  <span className="font-display text-base sm:text-lg text-[#E68153] whitespace-nowrap">
                    {a.price}
                  </span>
                </div>
                {a.note && (
                  <p className="text-[#B2A69E] text-xs mt-1 italic">{a.note}</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      </SectionShell>

      <div className="divider-ember mx-5 sm:mx-8 max-w-2xl xl:mx-auto" />

      {/* ------------------------- SFIZI ------------------------- */}
      <SectionShell
        id="sfizi"
        testid="sfizi-section"
        eyebrow="4. Sfizi"
        title="Nuggets e alette"
        subtitle="Comfort food onesto. Panature artigianali, salse della casa."
      >
        <div className="space-y-4">
          {SFIZI.map((s) => (
            <article
              key={s.id}
              data-testid={`sfizi-${s.id}`}
              className="card-rosto rounded-2xl overflow-hidden"
            >
              {s.image && (
                <div className="relative w-full h-52 overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.title}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] via-transparent to-transparent" />
                </div>
              )}
              <div className="p-5 sm:p-6">
                <h3 className="font-display text-2xl sm:text-3xl text-[#F5F1E6]">
                  {s.title}
                </h3>
                <p className="text-[#B2A69E] text-xs sm:text-sm mt-1">
                  {s.sub}
                </p>
                <div className="mt-5 flex gap-6">
                  {s.sizes.map((sz) => (
                    <div
                      key={sz.label}
                      className="flex flex-col"
                      data-testid={`sfizi-${s.id}-${sz.label.replace(/\s+/g, "-")}`}
                    >
                      <span className="font-label text-[10px] text-[#B2A69E]">
                        {sz.label}
                      </span>
                      <span className="font-display text-xl sm:text-2xl text-[#E68153]">
                        €{sz.price}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      </SectionShell>

      <div className="divider-ember mx-5 sm:mx-8 max-w-2xl xl:mx-auto" />

      {/* ------------------------- CONTORNI ------------------------- */}
      <SectionShell
        id="contorni"
        testid="contorni-section"
        eyebrow="5. Contorni"
        title="Sempre freschi, sempre caldi"
        subtitle="Ogni contorno disponibile in due formati: regular e large."
      >
        <ul className="space-y-3">
          {CONTORNI.map((c) => (
            <li
              key={c.id}
              data-testid={`contorno-${c.id}`}
              className="card-rosto rounded-2xl overflow-hidden flex items-center gap-4 pr-4 sm:pr-5"
            >
              {c.image && (
                <img
                  src={c.image}
                  alt={c.title}
                  loading="lazy"
                  className="w-20 h-20 sm:w-24 sm:h-24 object-cover flex-shrink-0"
                />
              )}
              <div className="flex-1 py-3 flex items-center justify-between gap-3">
                <span className="text-[#F5F1E6] text-base sm:text-lg font-display flex-1 min-w-0">
                  {c.title}
                </span>
                <div className="flex items-baseline gap-3 sm:gap-5 whitespace-nowrap">
                  <div className="flex flex-col items-end">
                    <span className="font-label text-[9px] text-[#B2A69E]">
                      Regular
                    </span>
                    <span className="font-display text-base sm:text-lg text-[#E68153]">
                      €{c.regular}
                    </span>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="font-label text-[9px] text-[#B2A69E]">
                      Large
                    </span>
                    <span className="font-display text-base sm:text-lg text-[#E68153]">
                      €{c.large}
                    </span>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </SectionShell>

      <div className="divider-ember mx-5 sm:mx-8 max-w-2xl xl:mx-auto" />

      {/* ------------------------- INSALATE ------------------------- */}
      <SectionShell
        id="insalate"
        testid="insalate-section"
        eyebrow="6. Insalate"
        title="Fresche, piene, con l'arrosto dentro."
        subtitle="Pensate per chi vuole leggerezza senza rinunciare al gusto."
      >
        <ul className="space-y-3">
          {INSALATE.map((s) => (
            <li
              key={s.id}
              data-testid={`insalata-${s.id}`}
              className="card-rosto rounded-2xl overflow-hidden flex items-stretch gap-4"
            >
              {s.image && (
                <img
                  src={s.image}
                  alt={s.title}
                  loading="lazy"
                  className="w-28 sm:w-36 h-auto object-cover flex-shrink-0"
                />
              )}
              <div className="flex-1 py-4 pr-5">
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="font-display text-xl sm:text-2xl text-[#F5F1E6]">
                    {s.title}
                  </h3>
                  <span className="font-display text-xl sm:text-2xl text-[#E68153] whitespace-nowrap">
                    €{s.price}
                  </span>
                </div>
                <p className="text-[#B2A69E] text-xs sm:text-sm mt-2 leading-relaxed">
                  {s.sub}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </SectionShell>

      <div className="divider-ember mx-5 sm:mx-8 max-w-2xl xl:mx-auto" />

      {/* ------------------------- SALSE ------------------------- */}
      <SectionShell
        id="salse"
        testid="salse-section"
        eyebrow="7. Salse"
        title="Sette salse per accompagnare"
        subtitle="Signature e classiche, tutte homemade dove serve."
      >
        <div className="card-rosto rounded-2xl overflow-hidden">
          <div className="relative w-full h-40 sm:h-52 overflow-hidden">
            <img
              src="/salse.jpg"
              alt="Salse della casa ROSTO'S"
              loading="lazy"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A1311] via-transparent to-transparent" />
          </div>
          <div className="p-5 sm:p-6 flex flex-wrap gap-2">
            {SALSE.map((sauce) => (
              <span
                key={sauce}
                data-testid={`salsa-${sauce.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="px-4 py-2 rounded-full border border-[#362A26] bg-[#0F0F0F] text-[#F5F1E6] text-sm hover:border-[#C86C3F] hover:text-[#E68153] transition-colors"
              >
                {sauce}
              </span>
            ))}
          </div>
        </div>
      </SectionShell>

      {/* ------------------------- QUALITY ------------------------- */}
      <section
        data-testid="menu-quality"
        className="px-5 sm:px-8 py-12 sm:py-16 bg-[#1A1311] border-y border-[#362A26]"
      >
        <div className="max-w-2xl mx-auto text-center">
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#251D1A] border border-[#362A26] text-[#E68153] mb-5">
            <Flame className="w-5 h-5" />
          </span>
          <p className="font-display text-xl sm:text-2xl text-[#F5F1E6] leading-snug">
            Pollo Campese allevato all'aperto, marinato{" "}
            <span className="text-[#E68153]">48 ore</span>, cotto lentamente
            per una pelle croccante e carne succosa.
          </p>
        </div>
      </section>

      {/* ------------------------- URGENCY ------------------------- */}
      <section data-testid="menu-urgency" className="px-5 sm:px-8 py-10">
        <div className="max-w-2xl mx-auto card-rosto rounded-2xl p-5 flex items-center gap-4 border-[#D9531E]/40">
          <span className="ember-pulse w-2.5 h-2.5 rounded-full bg-[#D9531E] flex-shrink-0" />
          <p className="font-label text-[12px] text-[#F5F1E6] leading-snug">
            🔥 Disponibile fino ad esaurimento
          </p>
        </div>
      </section>

      {/* ------------------------- FINAL CTA ------------------------- */}
      <section
        data-testid="menu-final-cta"
        className="px-5 sm:px-8 py-12 max-w-2xl mx-auto"
      >
        <button
          type="button"
          onClick={openOrderModal}
          data-testid="final-cta-order"
          className="btn-copper w-full px-6 py-5 rounded-2xl font-label text-sm flex items-center justify-center gap-3"
        >
          <Bike className="w-5 h-5" />
          Ordina a domicilio
        </button>
        <a
          href="/"
          data-testid="final-cta-home"
          className="mt-3 block text-center font-label text-[10px] text-[#B2A69E] hover:text-[#E68153] transition-colors"
        >
          ← Torna alla home
        </a>
      </section>

      {/* ------------------------- FOOTER ------------------------- */}
      <footer
        data-testid="menu-footer"
        className="px-5 sm:px-8 py-10 border-t border-[#362A26] bg-[#0F0F0F]"
      >
        <div className="max-w-2xl mx-auto space-y-5 text-center sm:text-left">
          <a
            href={BUSINESS.links.mapsLink}
            target="_blank"
            rel="noreferrer"
            data-testid="menu-footer-address"
            className="flex items-start gap-3 sm:items-center justify-center sm:justify-start group"
          >
            <span className="w-9 h-9 rounded-full bg-[#1A1311] border border-[#362A26] flex items-center justify-center text-[#E68153] flex-shrink-0">
              <MapPin className="w-4 h-4" />
            </span>
            <div>
              <p className="font-label text-[10px] text-[#C86C3F]">Indirizzo</p>
              <p className="text-[#F5F1E6] text-sm group-hover:text-[#E68153] transition-colors">
                {BUSINESS.address} — {BUSINESS.city}
              </p>
            </div>
          </a>

          <a
            href={`tel:${BUSINESS.phoneLink}`}
            data-testid="menu-footer-phone"
            className="flex items-start gap-3 sm:items-center justify-center sm:justify-start group"
          >
            <span className="w-9 h-9 rounded-full bg-[#1A1311] border border-[#362A26] flex items-center justify-center text-[#E68153] flex-shrink-0">
              <Phone className="w-4 h-4" />
            </span>
            <div>
              <p className="font-label text-[10px] text-[#C86C3F]">Telefono</p>
              <p className="text-[#F5F1E6] text-sm group-hover:text-[#E68153] transition-colors">
                {BUSINESS.phoneDisplay}
              </p>
            </div>
          </a>

          <p className="text-[#B2A69E]/60 text-xs pt-4 border-t border-[#362A26]">
            © {new Date().getFullYear()} Rosto's · Garbatella, Roma ·{" "}
            {BUSINESS.hoursShort}
          </p>
        </div>
      </footer>
    </main>
  );
}
